import { useCallback } from 'react';
import { Info } from '@beeline.com/react-spreadsheet-import';
import { messages } from 'src/components/fieldMetadata/ElementValidatorMessages';
import { FieldMetadataElementValidator } from 'src/components/fieldMetadata/FieldMetadataElementValidator';
import { RsiData, RsiFieldKey, RsiRowOnErrorCallback, RsiRowValidator } from '../../../Types';
import { CommonFieldDefinitionData } from '../../../utils/useImporterData';

export interface ValidationMetadataParams {
  urnToFieldMetadataMap: Map<String, CommonFieldDefinitionData>;
  fieldMetadataValidator: FieldMetadataElementValidator;
}

export const useValidateRowUsingMetadata = ({
  urnToFieldMetadataMap,
  fieldMetadataValidator
}: ValidationMetadataParams): RsiRowValidator => {
  return useCallback(
    (row: RsiData, addError: RsiRowOnErrorCallback) => {
      validateUsingMetadata(row, addError, urnToFieldMetadataMap, fieldMetadataValidator);
    },
    [urnToFieldMetadataMap, fieldMetadataValidator]
  );
};

export function validateUsingMetadata(
  row: RsiData,
  addError: (fieldKey: RsiFieldKey, error: Info) => void,
  urnToFieldMetadataMap: Map<String, CommonFieldDefinitionData>,
  fieldMetadataValidator: FieldMetadataElementValidator
): void {
  Object.keys(row)
    .filter((key) => key !== '__index' && key !== '__errors')
    .map((fieldKey) => {
      const unprocessedValue = row[fieldKey]?.toString();
      if (unprocessedValue === undefined || unprocessedValue === null) {
        return;
      }

      const metadata = urnToFieldMetadataMap.get(fieldKey);
      if (!metadata) {
        addError(
          fieldKey,

          { message: (messages.URN_DEFINITION_NOT_FOUND_ERROR.defaultMessage as string) ?? '', level: 'error' }
        );
        return;
      }

      preprocessFieldValues(unprocessedValue, metadata).flatMap((value: string) => {
        const errors = fieldMetadataValidator.getValidationErrors(fieldKey, value || undefined, new Map());

        if (errors && errors.length > 0) {
          errors.map((error) => {
            addError(fieldKey, { message: error?.defaultMessage?.toString() || '', level: 'error' });
          });
        }
      });
    });
}

function preprocessFieldValues(value: string | undefined, fieldDefinition: CommonFieldDefinitionData | undefined) {
  if (value === undefined || value === null) return [];
  if (fieldDefinition?.type === 'MULTI_VALUE_TYPED') {
    return value.split(',').map((el) => el.trim());
  }
  return [value.trim()];
}
